import React from 'react';
import { UserActions } from 'BMapsCmds';
import { ModalManager } from './ModalManager';
import { stateFileExtension } from '../utils';
import {
    textToBlobURL, freeBlobURL, downloadBlobURL, timestampedFilename,
} from './ui_utils';
import { SessionManager } from '../SessionManager';
import { supportsNativeSessionExport } from '../MainCanvas';
import LinkLikeButton from './common/LinkLikeButton';

export function DownloadSessionLink() {
    return (
        <LinkLikeButton
            onClick={() => {
                const stateString = JSON.stringify(UserActions.CaptureState());
                downloadBlobURL(textToBlobURL(stateString), SaveStatePanel.downloadFilename());
            }}
            style={{ color: 'var(--marketing-blue)' }}
        >
            <div className="downloadSessionLinkLooking">
                <i className="fa fa-download" />
                <span>Download a session file</span>
            </div>
        </LinkLikeButton>
    );
}

export class SaveStatePanel extends React.PureComponent {
    static Open(stateIn) {
        const state = stateIn || UserActions.CaptureState();
        ModalManager.Toggle({
            title: 'Save Session',
            content: <SaveStatePanel state={state} />,
        });
    }

    static downloadFilename() {
        return timestampedFilename('Save', stateFileExtension);
    }

    constructor(props) {
        super(props);
        this.state = { saved: false };
    }

    static getDerivedStateFromProps(props) {
        const stateString = JSON.stringify(props.state);
        const downloadURL = textToBlobURL(stateString);
        return { downloadURL };
    }

    componentWillUnmount() {
        const { downloadURL } = this.state;
        freeBlobURL(downloadURL);
    }

    saveToStorage() {
        const { state } = this.props;
        SessionManager.saveToLocalStorage('Last save', state);
        this.setState({ saved: true });
    }

    savedSessionExplanation() {
        return (
            <div>
                <div>
                    To restore, go to the &quot;GUIDE&quot; and press the BMaps Session
                    button under &quot;Recent Structures.&quot;
                </div>
                <div>
                    Please note: browser local storage currently supports only one saved
                    session.
                </div>
            </div>
        );
    }

    /**
     * A PyMOL session is an export, not a session BMaps can restore (it is a pickle, so BMaps
     * never reads one back) - it lives in the Export dialog, with a pointer from here, where
     * people look for it.
     */
    renderPyMOLSessionLink() {
        if (!supportsNativeSessionExport()) return null;
        return (
            <li className="downloadSessionButtonContainer">
                <LinkLikeButton
                    id="pymolSessionLink"
                    onClick={() => {
                        ModalManager.Hide(); // not on top of this dialog: one at a time
                        UserActions.OpenExport({}, { tabId: 'export_session_tab' });
                    }}
                >
                    <div className="downloadSessionLinkLooking">
                        <i className="fa fa-share-square-o" />
                        <span>Export a PyMOL session file (.pse)</span>
                    </div>
                    {/* the link colours everything inside it; the explanation reads as the
                        other two options' do */}
                    <div style={{ color: 'black' }}>
                        Opens Export. A .pse holds the 3D workspace for desktop PyMOL; BMaps
                        cannot open one again.
                    </div>
                </LinkLikeButton>
            </li>
        );
    }

    renderSaveSessionLink() {
        return (
            <li className="downloadSessionButtonContainer">
                <button
                    id="saveSessionLink"
                    type="button"
                    onClick={() => this.saveToStorage()}
                >
                    <div className="downloadSessionLinkLooking">
                        <i className="fa fa-save" />
                        <span>Save session to browser local storage</span>
                    </div>
                    {this.savedSessionExplanation()}
                </button>
            </li>
        );
    }

    renderAlreadySaved() {
        return (
            <li className="downloadSessionButtonContainer_saved">
                <button type="button" id="saveSessionLink" style={{ cursor: 'default' }}>
                    <div className="downloadSessionLinkLooking">
                        <i className="fa fa-check-square" />
                        <span>Saved</span>
                    </div>
                    {this.savedSessionExplanation()}
                </button>
            </li>
        );
    }

    render() {
        const { downloadURL, saved } = this.state;

        return (
            <div id="saveStatePanel">
                <div>You are in control of the storage of your compounds and any related data.</div>
                <div>Please choose a session storage option:</div>
                <ul>
                    <li className="downloadSessionButtonContainer">
                        <a
                            id="downloadSessionLink"
                            href={downloadURL}
                            download={SaveStatePanel.downloadFilename()}
                        >
                            <div className="downloadSessionLinkLooking">
                                <i className="fa fa-download" />
                                <span>Download a session file</span>
                            </div>
                            <div>
                                To restore, drag-drop the file back into BMaps or upload it using
                                Add Compound.
                            </div>
                        </a>
                    </li>
                    {saved
                        ? this.renderAlreadySaved()
                        : this.renderSaveSessionLink()}
                    {this.renderPyMOLSessionLink()}
                </ul>
            </div>
        );
    }
}
